import { useMemo } from 'react';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek.js';
import { useIntl } from 'react-intl';
import { formatDate } from '@repo/design-system-kit';
import type { IndicatorChartRate } from '../components/indicator-chart/types';

dayjs.extend(isoWeek);

const UNIT_MAP = {
  daily: 'day',
  weekly: 'week',
  monthly: 'month',
} as const;

const DATE_TOKEN = {
  daily: 'date-year-l',
  monthly: 'month-year-l',
} as const;

export function usePeriodTitle(date: Date, rate: IndicatorChartRate): string {
  const { formatMessage, locale } = useIntl();
  return useMemo(() => {
    if (dayjs().isSame(date, UNIT_MAP[rate])) {
      if (rate === 'daily') return formatMessage({ id: 'shared-kit.overview.dates.today' });
      if (rate === 'weekly') return formatMessage({ id: 'shared-kit.overview.dates.current-week' });
      return formatMessage({ id: 'shared-kit.overview.dates.current-month' });
    }

    if (rate === 'weekly') {
      const startDay = formatDate({
        date: dayjs(date).startOf('isoWeek').toDate(),
        locale,
        token: DATE_TOKEN.daily,
      });
      const endDay = formatDate({
        date: dayjs(date).endOf('isoWeek').toDate(),
        locale,
        token: DATE_TOKEN.daily,
      });
      return formatMessage({ id: 'shared-kit.overview.dates.week-of' }, { startDay, endDay });
    }

    return formatDate({ date, locale, token: DATE_TOKEN[rate] });
  }, [date, rate, locale, formatMessage]);
}
